import { useState, useEffect, useCallback, Fragment } from 'react';
import api from '../../hooks/useApi';

// Resourcing (SOW detail): what each resource line sold, what the plan has
// scheduled against it, who is on it, and what has been booked.
//
//   Sold       the line's days
//   Planned    days of work on the plan tasks linked to the line
//   Staffed    the part of that with a named person on it
//   Unstaffed  the part with nobody yet: demand for the line's band
//   Booked     time booked to those tasks, in days of 7.5 hours
//
// A plan task delivers a line when it names it (Phase 2, migration 0377). Tasks
// that name no line are listed below the lines, each with a picker, so linking
// happens where the gap is shown.

const r1 = (n) => Math.round((Number(n) || 0) * 10) / 10;
const fmtD = (d) => (d ? new Date(d + 'T00:00:00Z').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' }) : '');

function Bars({ sold, planned, booked }) {
  const scale = Math.max(sold, planned, booked, 0.0001);
  const w = (n) => `${Math.min(100, (n / scale) * 100)}%`;
  const over = planned > sold + 0.01;
  return (
    <div className="w-full space-y-0.5" title={`Sold ${r1(sold)}d · planned ${r1(planned)}d · booked ${r1(booked)}d`}>
      <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden"><div className="h-full bg-gray-400" style={{ width: w(sold) }} /></div>
      <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden"><div className={`h-full ${over ? 'bg-red-500' : 'bg-emerald-400'}`} style={{ width: w(planned) }} /></div>
      <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden"><div className="h-full bg-navy" style={{ width: w(booked) }} /></div>
    </div>
  );
}

function Remaining({ days }) {
  if (Math.abs(days) < 0.05) return <span className="text-emerald-700 font-semibold">all planned</span>;
  return days > 0
    ? <span className="text-amber-700 font-semibold">{r1(days)}d not yet planned</span>
    : <span className="text-red-700 font-semibold">planned {r1(-days)}d over what was sold</span>;
}

export default function SowResourcingTab({ sowId, canEdit = true, onLinesChanged }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(() => new Set());
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState(null);            // { kind, text } shown beside the buttons
  const [rowNote, setRowNote] = useState({});        // task_id -> text, shown on that row

  const load = useCallback(async () => {
    try { setData(await api.get(`sows/${sowId}/resourcing`)); setError(''); }
    catch (e) { setError(e.message || 'Could not load resourcing.'); }
  }, [sowId]);
  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setOpen(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const linkTask = async (taskId, lineId) => {
    setRowNote(n => ({ ...n, [taskId]: 'Saving…' }));
    try {
      await api.put(`sows/${sowId}/project-tasks/${taskId}`, { resource_line_id: lineId || null });
      setRowNote(n => ({ ...n, [taskId]: lineId ? 'Linked' : 'Unlinked' }));
      await load();
    } catch (e) {
      setRowNote(n => ({ ...n, [taskId]: e.message || 'Could not save' }));
    }
  };

  const linkByBand = async () => {
    setBusy('band'); setNote(null);
    try {
      const r = await api.post(`sows/${sowId}/resourcing/link-by-band`, {});
      setNote({
        kind: r.linked.length ? 'ok' : 'warn',
        text: r.linked.length
          ? `Linked ${r.linked.length} task${r.linked.length === 1 ? '' : 's'} by their lead's band.${r.skipped.length ? ` ${r.skipped.length} left for you to link below.` : ''}`
          : `Nothing to link by band. ${r.skipped.length ? `${r.skipped.length} task${r.skipped.length === 1 ? '' : 's'} need a line choosing below.` : ''}`,
        skipped: r.skipped,
      });
      await load();
    } catch (e) { setNote({ kind: 'error', text: e.message || 'Linking failed.' }); }
    finally { setBusy(''); }
  };

  const fromPlan = async () => {
    setBusy('plan'); setNote(null);
    try {
      const r = await api.post(`sows/${sowId}/resource-lines/from-plan`, { apply: true });
      setNote({
        kind: r.created.length ? 'ok' : 'warn',
        text: r.created.length
          ? `Created ${r.created.length} line${r.created.length === 1 ? '' : 's'} from the plan, by each person's own band, and linked their tasks.`
          : 'No unlinked, banded work in the plan to create lines from.',
        unpriced: r.unpriced,
      });
      await load();
      onLinesChanged?.();
    } catch (e) { setNote({ kind: 'error', text: e.message || 'Could not create lines.' }); }
    finally { setBusy(''); }
  };

  if (error) return <div className="text-[9px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">{error}</div>;
  if (!data) return <div className="py-8 text-center text-[10px] text-gray-400">Working out resourcing…</div>;

  const { lines, unlinked, totals } = data;
  const lineOptions = lines.map(l => ({ id: l.id, label: `${l.band_code}${l.description ? ` · ${l.description}` : ''} (${r1(l.sold_days)}d)` }));
  const noteCls = { ok: 'text-emerald-800 bg-emerald-50 border-emerald-200', warn: 'text-amber-800 bg-amber-50 border-amber-200', error: 'text-red-800 bg-red-50 border-red-200' };

  return (
    <div className="space-y-3">
      {/* Totals */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {[
          ['Sold', totals.sold_days, 'text-gray-700'],
          ['Planned', totals.planned_days, totals.planned_days > totals.sold_days + 0.01 ? 'text-red-600' : 'text-emerald-700'],
          ['Staffed', totals.staffed_days, 'text-navy'],
          ['Unstaffed', totals.unstaffed_days, totals.unstaffed_days > 0 ? 'text-amber-700' : 'text-gray-400'],
          ['Booked', totals.booked_days, 'text-navy'],
        ].map(([label, v, cls]) => (
          <div key={label} className="bg-white border border-gray-200 rounded p-2">
            <div className="text-[8px] text-gray-500 uppercase font-bold">{label}</div>
            <div className={`text-sm font-extrabold ${cls}`}>{r1(v)}d</div>
          </div>
        ))}
      </div>

      {/* Actions, with their result beside them */}
      {canEdit && (
        <div className="flex flex-wrap items-start gap-2">
          <button onClick={linkByBand} disabled={!!busy || !lines.length || !unlinked.tasks.length}
                  className="px-2.5 py-1 text-[9px] font-semibold rounded border border-navy/30 text-navy bg-white hover:bg-navy/5 disabled:opacity-40"
                  title="Link each unlinked task to the line with its lead's band, where exactly one line has it">
            {busy === 'band' ? 'Linking…' : 'Link tasks by band'}
          </button>
          <button onClick={fromPlan} disabled={!!busy || !unlinked.tasks.length}
                  className="px-2.5 py-1 text-[9px] font-semibold rounded border border-gray-200 text-gray-600 bg-white hover:bg-gray-50 disabled:opacity-40"
                  title="Create lines for unlinked work in the plan, by each person's own band, priced off the rate card">
            {busy === 'plan' ? 'Creating…' : 'Create lines from the plan'}
          </button>
          {note && (
            <div className={`flex-1 min-w-[240px] text-[9px] border rounded px-2 py-1 ${noteCls[note.kind]}`}>
              {note.text}
              {(note.skipped || []).length > 0 && (
                <ul className="mt-0.5 list-disc ml-4">{note.skipped.slice(0, 6).map(s => <li key={s.task_id}>{s.name}: {s.reason}</li>)}</ul>
              )}
              {(note.unpriced || []).length > 0 && (
                <ul className="mt-0.5 list-disc ml-4">{note.unpriced.slice(0, 6).map((u, i) => <li key={i}>{u.task_name}, {u.person}: {u.reason}</li>)}</ul>
              )}
            </div>
          )}
        </div>
      )}

      {/* Lines */}
      <div className="bg-white border border-gray-200 rounded overflow-x-auto">
        <table className="w-full text-[9px]">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200 text-[8px] uppercase text-gray-500 font-bold">
              <th className="px-3 py-1.5 text-left">Resource line</th>
              <th className="px-2 py-1.5 text-right">Sold</th>
              <th className="px-2 py-1.5 text-right">Planned</th>
              <th className="px-2 py-1.5 text-right">Staffed</th>
              <th className="px-2 py-1.5 text-right">Unstaffed</th>
              <th className="px-2 py-1.5 text-right">Booked</th>
              <th className="px-2 py-1.5 text-left min-w-[120px]">Sold · planned · booked</th>
              <th className="px-3 py-1.5 text-left">Against what was sold</th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-gray-400">
                This SOW has no resource lines. Add them on the Resources tab, or create them from the plan.
              </td></tr>
            )}
            {lines.map(l => {
              const isOpen = open.has(l.id);
              return (
                <Fragment key={l.id}>
                  <tr className="border-b border-gray-100 hover:bg-gray-50/60 cursor-pointer" onClick={() => toggle(l.id)}>
                    <td className="px-3 py-1.5">
                      <div className="font-semibold text-navy">{l.band_code} <span className="font-normal text-gray-500">{l.band_name}</span></div>
                      <div className="text-[8px] text-gray-400">{[l.work_package, l.description].filter(Boolean).join(' · ')} · {l.tasks.length} task{l.tasks.length === 1 ? '' : 's'} {isOpen ? '▲' : '▼'}</div>
                    </td>
                    <td className="px-2 py-1.5 text-right font-semibold">{r1(l.sold_days)}d</td>
                    <td className="px-2 py-1.5 text-right">{r1(l.planned_days)}d</td>
                    <td className="px-2 py-1.5 text-right">{r1(l.staffed_days)}d</td>
                    <td className={`px-2 py-1.5 text-right ${l.unstaffed_days > 0 ? 'text-amber-700 font-semibold' : 'text-gray-400'}`}>{r1(l.unstaffed_days)}d</td>
                    <td className="px-2 py-1.5 text-right">{r1(l.booked_days)}d</td>
                    <td className="px-2 py-1.5"><Bars sold={l.sold_days} planned={l.planned_days} booked={l.booked_days} /></td>
                    <td className="px-3 py-1.5"><Remaining days={l.unplanned_days} /></td>
                  </tr>
                  {isOpen && (
                    <tr className="border-b border-gray-100 bg-gray-50/60">
                      <td colSpan={8} className="px-3 py-2">
                        <div className="grid sm:grid-cols-2 gap-3">
                          <div>
                            <div className="text-[8px] font-bold text-gray-400 uppercase mb-1">People on it</div>
                            {l.people.length === 0 ? <div className="text-gray-400">Nobody yet.</div> : l.people.map(p => (
                              <div key={p.person_id} className="flex justify-between gap-2">
                                <span className="text-gray-700">{p.name}</span>
                                <span className="text-gray-500">{r1(p.planned_days)}d planned{data.show_people ? ` · ${r1(p.booked_days)}d booked` : ''}</span>
                              </div>
                            ))}
                          </div>
                          <div>
                            <div className="text-[8px] font-bold text-gray-400 uppercase mb-1">Plan tasks</div>
                            {l.tasks.length === 0 ? <div className="text-gray-400">No plan tasks are linked to this line yet.</div> : l.tasks.map(t => (
                              <div key={t.task_id} className="flex items-center justify-between gap-2 py-0.5">
                                <span className="min-w-0 truncate text-gray-700">
                                  {t.name}
                                  <span className="text-gray-400"> · {t.start_date ? `${fmtD(t.start_date)} to ${fmtD(t.end_date)}` : 'not scheduled'} · {r1(t.planned_days)}d</span>
                                  {t.allowance_type === 'team'
                                    ? <span className="ml-1 text-[7.5px] font-semibold text-amber-800 bg-amber-50 px-1 rounded">{r1(t.allowance_used_days)}d of {r1(t.allowance_days)}d on tasks</span>
                                    : t.allowance
                                      ? <span className="ml-1 text-[7.5px] font-semibold text-sky-800 bg-sky-50 px-1 rounded">older person allowance</span>
                                      : t.unstaffed_days > 0 && <span className="ml-1 text-[7.5px] font-semibold text-amber-800 bg-amber-50 px-1 rounded">nobody on it</span>}
                                </span>
                                {canEdit && (
                                  <span className="flex items-center gap-1 flex-shrink-0">
                                    <select className="form-select text-[8.5px] py-0" value={l.id} onClick={e => e.stopPropagation()}
                                            onChange={e => linkTask(t.task_id, e.target.value)}>
                                      {lineOptions.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                                      <option value="">Not linked</option>
                                    </select>
                                    {rowNote[t.task_id] && <span className="text-[7.5px] text-gray-500">{rowNote[t.task_id]}</span>}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Work not linked to any line */}
      {unlinked.tasks.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded p-2">
          <div className="text-[9px] font-bold text-amber-800 mb-1">
            {unlinked.tasks.length} plan task{unlinked.tasks.length === 1 ? '' : 's'} not linked to a resource line · {r1(unlinked.planned_days)}d of work
          </div>
          <div className="space-y-0.5">
            {unlinked.tasks.map(t => (
              <div key={t.task_id} className="flex items-center justify-between gap-2 text-[9px]">
                <span className="min-w-0 truncate text-gray-700">
                  {t.name}
                  <span className="text-gray-500"> · {t.start_date ? `${fmtD(t.start_date)} to ${fmtD(t.end_date)}` : 'not scheduled'} · {r1(t.planned_days)}d</span>
                  {t.people.length > 0
                    ? <span className="text-gray-500"> · {t.people.map(p => p.name).join(', ')}</span>
                    : <span className="ml-1 text-[7.5px] font-semibold text-amber-800 bg-white px-1 rounded">nobody on it</span>}
                </span>
                {canEdit && lines.length > 0 && (
                  <span className="flex items-center gap-1 flex-shrink-0">
                    <select className="form-select text-[8.5px] py-0" value="" onChange={e => e.target.value && linkTask(t.task_id, e.target.value)}>
                      <option value="">Link to a line…</option>
                      {lineOptions.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                    </select>
                    {rowNote[t.task_id] && <span className="text-[7.5px] text-gray-500">{rowNote[t.task_id]}</span>}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {data.unplanned_time_days > 0 && (
        <div className="text-[8.5px] text-gray-500">
          {r1(data.unplanned_time_days)}d of time was booked to this SOW without a plan task, so it is in Booked above but not against any line.
        </div>
      )}
      <div className="text-[8px] text-gray-400 leading-relaxed">
        Planned is days of work on the linked plan tasks: each person's share of the task, or their planned hours. A task with nobody on it counts as unstaffed demand for the line's band, and shows in Resource Management's unstaffed work.
        Booked is time booked to the linked tasks, at 7.5 hours a day.{!data.show_people && ' Who booked what shows to holders of See everyone\'s hours and rates.'}
      </div>
    </div>
  );
}
